// @flow
import React from 'react'
import { Link, useStaticQuery, graphql } from 'gatsby'
import uuid from 'uuid/v4'
import classNames from 'classnames'
import Dropdown from './dropdown'
import { LogoGlyph } from './logo'
import Icon from './icon'
import styles from './header.module.css'

const headerLinks = [
  {
    name: 'Home',
    path: '/',
    matchPartially: false,
  },
  {
    name: 'About',
    path: '/about',
    matchPartially: false,
    links: [
      {
        name: 'Organization',
        path: '/organization',
      },
      uuid(),
      ...[2020, 2019, 2018, 2017, 2016, 2015, 2014].map((year) => ({
        name: `PUBMET${year}`,
        path: `%siteUrl/pubmet${year}`,
        isDisabled: year === 2019,
      })),
    ],
  },
  {
    name: 'Programme',
    path: '/programme',
    matchPartially: false,
  },
  {
    name: 'Speakers',
    path: '/speakers',
    matchPartially: true,
  },
  {
    name: 'Sponsors',
    path: '/sponsors',
    matchPartially: false,
    links: [
      uuid(),
      {
        name: 'Call for Sponsors',
        path: '/call-for-sponsors',
      },
    ],
  },
  {
    name: 'Venue',
    path: '/venue',
    matchPartially: false,
  },
  {
    name: 'Contact',
    path: '/contact-us',
    matchPartially: false,
  },
]

type Props = {
  isFloating: boolean,
}

function Header({ isFloating }: Props) {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const data = useStaticQuery(graphql`
    query Header {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `)
  const { siteUrl } = data.site.siteMetadata

  React.useEffect(() => {
    if (typeof document !== 'undefined' && document.body != null) {
      document.body.classList.toggle('preventScroll', isExpanded)
    }
  }, [isExpanded])

  function closeMenu() {
    setIsExpanded(false)
  }

  return (
    <header
      className={classNames(styles.header, {
        [styles.isFloating]: isFloating,
      })}
    >
      <Link to="/" className={styles.logoGlyph} onClick={closeMenu}>
        <LogoGlyph />
      </Link>
      <nav
        className={classNames(styles.navigation, {
          [styles.isExpanded]: isExpanded,
        })}
      >
        <button
          className={styles.toggleList}
          type="button"
          aria-expanded={isExpanded}
          onClick={() => {
            setIsExpanded(!isExpanded)
          }}
        >
          <Icon role="img" id={isExpanded ? 'close' : 'menu'} />
        </button>
        <ol
          className={classNames(styles.list, {
            [styles.isExpanded]: isExpanded,
          })}
        >
          {headerLinks.map((link) => (
            <li key={link.path}>
              {link.links != null ? (
                <Dropdown
                  links={[
                    {
                      name: link.name,
                      path: link.path,
                    },
                    ...link.links.map((l) =>
                      typeof l === 'string'
                        ? l
                        : {
                            name: l.name,
                            path: l.path.replace('%siteUrl', siteUrl),
                            isDisabled: l.isDisabled || false,
                          },
                    ),
                  ]}
                >
                  {({ referenceProps }) => (
                    <Link
                      {...referenceProps}
                      className={classNames(
                        styles.link,
                        referenceProps.className,
                      )}
                      to={link.path}
                      activeClassName={styles.isActive}
                      partiallyActive={link.matchPartially}
                      onClick={closeMenu}
                    >
                      {link.name}
                      {referenceProps.children}
                    </Link>
                  )}
                </Dropdown>
              ) : (
                <Link
                  className={styles.link}
                  to={link.path}
                  activeClassName={styles.isActive}
                  partiallyActive={link.matchPartially}
                  onClick={closeMenu}
                >
                  {link.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </header>
  )
}

Header.defaultProps = {
  isFloating: false,
}

export default Header
